// Iter 51 — Behavioral Insights Drawer.
// Replaces the inline-expand UX on the 4 InsightTiles (SL / Frequency /
// Emotional / Edge) with a right-side Sheet that shows richer, actionable
// detail: full metric breakdown + suggested fixes per insight.
//
// All derivations are pure-frontend reads off `stats` — no new API calls.
import React from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

// Tone-driven palette mapped to design-system tokens.
const TONE_BG = {
  good: "color-mix(in srgb, var(--bt-win) 10%, transparent)",
  warn: "color-mix(in srgb, var(--bt-warn) 12%, transparent)",
  bad:  "color-mix(in srgb, var(--bt-loss) 12%, transparent)",
};
const TONE_BORDER = {
  good: "color-mix(in srgb, var(--bt-win) 35%, transparent)",
  warn: "color-mix(in srgb, var(--bt-warn) 38%, transparent)",
  bad:  "color-mix(in srgb, var(--bt-loss) 38%, transparent)",
};
const TONE_TEXT = { good: "var(--bt-win)", warn: "var(--bt-warn)", bad: "var(--bt-loss)" };

function Stat({ label, value, tone, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-sm border p-3"
      style={{
        background: "var(--bt-surface-2)",
        borderColor: "var(--bt-border)",
      }}
    >
      <p className="bt-label text-[10px]">{label}</p>
      <p className="font-heading bt-num text-2xl mt-1.5" style={{ color: tone ? TONE_TEXT[tone] : "var(--bt-text)" }}>
        {value}
      </p>
    </div>
  );
}

function FixList({ items, tone = "warn", testId = "drawer-fix-list" }) {
  if (!items || !items.length) return null;
  return (
    <div className="mt-5">
      <p className="bt-label mb-2">Suggested fixes</p>
      <ul data-testid={testId} className="space-y-2">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-sm leading-relaxed rounded-sm border px-3 py-2"
            style={{
              background: TONE_BG[tone],
              borderColor: TONE_BORDER[tone],
              color: "var(--bt-text)",
            }}
          >
            <span className="font-mono text-xs" style={{ color: TONE_TEXT[tone] }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Per-insight body components ─────────────────────────────────────────

function StopLossBody({ stats, language }) {
  const sl = stats?.sl_discipline || {};
  const HI = language === "hi";
  // Iter 127 — Structural gap: this account's data source doesn't carry
  // stop_loss on the trade (Delta CSV etc.). Show an honest "unavailable"
  // body instead of judging discipline off null data.
  if (sl.source_gap) {
    const src = (sl.sources_missing_sl_data || []).map((s) =>
      s === "csv" ? "CSV" : s === "delta_api" ? "Delta API" : s
    ).join(", ") || "your import format";
    return (
      <>
        <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
          {HI
            ? "Aapke import format se Stop Loss data ki jankaari nahi milti. Isliye is metric ko honestly verdict nahi kiya ja sakta."
            : "Stop-loss data isn't captured by your current import format, so we can't verdict discipline honestly from this data alone."}
        </p>
        <div
          data-testid="drawer-sl-source-gap"
          className="mt-4 rounded-sm border p-3 text-sm leading-relaxed"
          style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-hover)" }}
        >
          <p className="text-[var(--bt-text)]">
            <span className="font-semibold">Source(s) missing SL data:</span>{" "}
            <span className="text-[var(--bt-text-muted)]">{src}</span>
          </p>
          <p className="text-[var(--bt-text-muted)] mt-2">
            {HI
              ? "Exchange CSVs (jaise Delta) STOP_LOSS ko alag order row mein export karte hain, trade ke saath nahi. To unlock: broker API se connect kariye, ya har trade pe SL manually log kariye."
              : "Exchanges like Delta export STOP_LOSS as a separate order row, not as a field on the trade. To unlock: connect via broker API (if it exposes SL orders), or log a stop_loss on trades manually."}
          </p>
        </div>
      </>
    );
  }
  const tone = (sl.discipline_pct ?? 100) >= 60 ? "good" : "bad";
  const fixes = tone === "bad" ? (HI ? [
    "Har trade pe Stop Loss zaroor lagayein — yeh order entry ka part hona chahiye, baad mein soche jaane wali cheez nahi.",
    `Aapke ${sl.trades_without_sl ?? 0} trades mein SL nahi tha. Trades ko "no SL" filter karke dekhiye aur mentally har trade pe SL set kariye — yahi habit banti hai.`,
    "TradingView ya aapke charting tool mein default order template ko aise configure kar lijiye ki SL mandatory ho.",
  ] : [
    "Aapki discipline strong hai — aise hi karte rahiye. Yahi habit consistent traders ko alag karti hai.",
    "Ab next focus R-multiple par rakhiye — har trade pe minimum 1:2 reward:risk aim kariye.",
  ]) : (HI ? [
    "Aapki discipline strong hai — aise hi karte rahiye. Yahi #1 habit consistent traders ko alag karti hai.",
    "Ab next focus R-multiple par rakhiye — har trade pe minimum 1:2 reward:risk aim kariye.",
  ] : [
    "Strong discipline — keep doing this. The #1 habit that separates consistent traders.",
    "Next: focus on R-multiple. Aim for a 1:2 reward:risk minimum on every trade.",
  ]);
  // Original English fixes when language is EN and tone is bad
  const enBadFixes = [
    "Set a stop loss on every trade — make it part of order entry, not an afterthought.",
    `${sl.trades_without_sl ?? 0} of your trades had no SL. Filter Trades by "no SL" and add one mentally to each — this builds the habit.`,
    "On TradingView / your charting tool, configure your default order template to require an SL.",
  ];
  const finalFixes = (!HI && tone === "bad") ? enBadFixes : fixes;
  return (
    <>
      <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
        {HI
          ? "Trade me entry karne se pehle Stop Loss lagane wale trades ka % — yeh long-term trading survival ka sabse bada predictor hai."
          : "% of trades where you actually set a stop loss before entering — the single biggest predictor of long-term survival as a trader."}
      </p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Stat label="Discipline %" value={`${(sl.discipline_pct ?? 0).toFixed(0)}%`} tone={tone} testId="drawer-sl-discipline" />
        <Stat label="With SL" value={sl.trades_with_sl ?? 0} testId="drawer-sl-with" />
        <Stat label="Without SL" value={sl.trades_without_sl ?? 0} tone={(sl.trades_without_sl ?? 0) > 0 ? "warn" : null} testId="drawer-sl-without" />
      </div>
      <p className="text-sm mt-4 leading-relaxed text-[var(--bt-text)]">
        {HI ? (
          <>Aapke losses mein se <span className="bt-num font-semibold">{(sl.losses_without_sl_pct ?? 0).toFixed(1)}%</span> trades mein Stop Loss nahi tha
          ({sl.losses_without_sl ?? 0} of {sl.total_losses ?? 0} losses).</>
        ) : (
          <>Of your losses, <span className="bt-num font-semibold">{(sl.losses_without_sl_pct ?? 0).toFixed(1)}%</span> had no stop loss
          ({sl.losses_without_sl ?? 0} of {sl.total_losses ?? 0} losses).</>
        )}
      </p>
      <FixList items={finalFixes} tone={tone} testId="drawer-sl-fixes" />
    </>
  );
}

function FrequencyBody({ stats, language }) {
  const ot = stats?.overtrading || {};
  const flagged = (ot.high_vol_days ?? 0) > 0;
  const tone = flagged ? "warn" : "good";
  const HI = language === "hi";
  const fixes = flagged
    ? (HI ? [
        `Aapne ${ot.high_vol_days} din${ot.high_vol_days === 1 ? "" : "on"} overtrade kiya — kam, lekin higher-conviction setups par focus rakhiye.`,
        `Aapki high-volume day Win Rate (${(ot.win_rate_high_vol ?? 0).toFixed(1)}%) aapki normal-day Win Rate (${(ot.win_rate_normal ?? 0).toFixed(1)}%) se kam hai. Zyada trades ka matlab zyada profit nahi hota.`,
        "Daily max-trade cap set kar lijiye (jaise 3) — limit hit ho jaye toh next setup ko journal kariye, lijiye mat.",
      ] : [
        `You overtraded on ${ot.high_vol_days} day${ot.high_vol_days === 1 ? "" : "s"} — aim for fewer, higher-conviction setups.`,
        `Your high-volume-day win rate (${(ot.win_rate_high_vol ?? 0).toFixed(1)}%) is below your normal-day win rate (${(ot.win_rate_normal ?? 0).toFixed(1)}%). More trades ≠ more profit.`,
        "Set a daily max-trade cap (e.g. 3) — once hit, journal the next setup instead of taking it.",
      ])
    : (HI ? [
        "Aapki trade frequency healthy hai. Quality over quantity ka faayda mil raha hai.",
        "Ab next: apne high-conviction setups review kariye — woh kya alag kar rahe the?",
      ] : [
        "Healthy trade frequency. Quality over quantity is paying off.",
        "Next: review your high-conviction setups — what made them stand out?",
      ]);
  return (
    <>
      <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
        {HI
          ? "Aap kitne trades karte hain vs kitna profit actually nikalte hain. Overtrading days par usually Win Rate kharaab hota hai — data confirm ya deny kar dega."
          : "How often you trade vs. how much profit you actually extract. Overtrading days usually have a worse win rate — the data either confirms or denies it for you."}
      </p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Stat label="Avg / day" value={(ot.avg_per_day ?? 0).toFixed(1)} testId="drawer-freq-avg" />
        <Stat label="Peak day" value={ot.max_trades_in_a_day ?? 0} tone={(ot.max_trades_in_a_day ?? 0) >= 5 ? "warn" : null} testId="drawer-freq-peak" />
        <Stat label="High-vol days" value={ot.high_vol_days ?? 0} tone={flagged ? "warn" : "good"} testId="drawer-freq-hv" />
      </div>
      <p className="text-sm mt-4 leading-relaxed text-[var(--bt-text)]">
        High-vol day WR: <span className="bt-num font-semibold">{(ot.win_rate_high_vol ?? 0).toFixed(2)}%</span> · Normal day WR:
        <span className="bt-num font-semibold ml-1">{(ot.win_rate_normal ?? 0).toFixed(2)}%</span>
      </p>
      <FixList items={fixes} tone={tone} testId="drawer-freq-fixes" />
    </>
  );
}

function EmotionalBody({ stats, language }) {
  const rv = stats?.revenge || {};
  const HI = language === "hi";
  const emotionGap = !!rv.emotion_gap;
  // Iter 127 — the emotional_tags side is only trustworthy when the
  // user has actually logged emotion on trades. When emotion_gap is
  // true, we drop it from the "flagged" decision entirely so we don't
  // fabricate an "Emotional Control" verdict from zero emotion data.
  const winRateGap = (rv.normal_win_rate ?? 0) - (rv.revenge_win_rate ?? 0) >= 10;
  const flagged = winRateGap || (!emotionGap && (rv.revenge_emotional ?? 0) > 0);
  const tone = flagged ? "bad" : (emotionGap ? "warn" : "good");
  const fixes = flagged
    ? (HI ? [
        "Har loss ke baad 10 minute ka break lijiye — phir hi next chart open kariye. Yeh ek rule bana lijiye.",
        `Aapke ${rv.revenge_emotional ?? 0} trades Revenge / FOMO / Greedy tagged hain. Unhe review kariye — trigger kya tha?`,
        "Ek max-loss-per-day stop set kar lijiye. Limit hit ho gayi toh trading day khatam — kal review kariye.",
      ] : [
        "After every loss, take a 10-minute break before opening another chart. Make it a rule.",
        `${rv.revenge_emotional ?? 0} of your trades are tagged Revenge / FOMO / Greedy. Review them — what was the trigger?`,
        "Set a max-loss-per-day stop. Once hit, the trading day is over — review tomorrow.",
      ])
    : (HI ? [
        "Aapka emotional control strong hai — aap losses chase nahi karte. Rare aur valuable quality.",
        "Disciplined rahiye: yahi consistency ki foundation banti hai.",
      ] : [
        "Strong emotional control — you don't chase losses. Rare and valuable.",
        "Stay disciplined: this becomes the foundation of consistency.",
      ]);
  return (
    <>
      <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
        {HI
          ? "Loss ke baad jo trades aap lete hain, unka Win Rate vs baaki trades. Bada gap matlab aap gusse mein trade kar rahe hain — account blow up karne ka sabse tez tareeka."
          : "Your win rate on trades taken right after a loss vs. all other trades. A big gap means you're trading angry — the single fastest way to blow up an account."}
      </p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Stat label="After-loss WR" value={`${(rv.revenge_win_rate ?? 0).toFixed(1)}%`} tone={flagged ? "bad" : "good"} testId="drawer-emo-after" />
        <Stat label="Normal WR" value={`${(rv.normal_win_rate ?? 0).toFixed(1)}%`} testId="drawer-emo-normal" />
        {emotionGap ? (
          <Stat label="Emotional tags" value={HI ? "Log nahi" : "Not logged"} tone="warn" testId="drawer-emo-tagged-gap" />
        ) : (
          <Stat label="Emotional tags" value={rv.revenge_emotional ?? 0} tone={(rv.revenge_emotional ?? 0) > 0 ? "warn" : null} testId="drawer-emo-tagged" />
        )}
      </div>
      <p className="text-sm mt-4 leading-relaxed text-[var(--bt-text)]">
        {emotionGap
          ? (HI
              ? `${rv.revenge_count ?? 0} trades loss ke turant baad liye gaye. Emotion tags in trades pe logged nahi hain — Revenge / FOMO / Greedy tag kariye taaki full insight unlock ho.`
              : `${rv.revenge_count ?? 0} trades taken right after a loss. Emotion tags aren't logged on these trades — tag them with Revenge / FOMO / Greedy to unlock the full insight.`)
          : (HI
              ? `${rv.revenge_count ?? 0} trades loss ke turant baad liye gaye. ${(rv.revenge_emotional ?? 0)} tagged Revenge / FOMO / Greedy.`
              : `${rv.revenge_count ?? 0} trades taken right after a loss. ${(rv.revenge_emotional ?? 0)} tagged Revenge / FOMO / Greedy.`)}
      </p>
      <FixList items={fixes} tone={tone} testId="drawer-emo-fixes" />
    </>
  );
}

function EdgeBody({ stats, currency, language }) {
  const edge = stats?.edge || {};
  const HI = language === "hi";
  const fmt = (n) => currency === "INR" ? `₹${Math.round(n).toLocaleString()}` : `$${Math.round(n).toLocaleString()}`;
  if (!edge.eligible) {
    return (
      <>
        <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
          {HI
            ? "Hum aapke Win Rate ko har source / symbol / emotion / direction mein compare karke aapki best edge aur worst leak nikalte hain. Unlock ke liye chahiye at least 2 buckets jisme 2+ trades hon."
            : "We compare your win rate across every source / symbol / emotion / direction to find your single best edge and your single worst leak. Need at least 2 buckets with 2+ trades each before this unlocks."}
        </p>
        <FixList
          items={HI ? [
            "Aur varied trades log kariye — alag-alag sources (Signal vs Self), symbols, aur emotions ke.",
            "Har trade ko tag kariye ki setup kaise mila. Analysis isse hi chalti hai.",
          ] : [
            "Log more varied trades — across different sources (Signal vs Self), symbols, and emotions.",
            "Tag each trade with how you found the setup. This is what powers the analysis.",
          ]}
          tone="warn"
          testId="drawer-edge-fixes"
        />
      </>
    );
  }
  const b = edge.best, w = edge.worst;
  return (
    <>
      <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
        {HI
          ? <>Aapke last {stats?.total_trades ?? 0} trades mein yeh raha sabse best aur sabse worst bucket. Strength ko aur dohraiye; leak ko cut kariye.</>
          : <>Across your last {stats?.total_trades ?? 0} trades, here's your highest- and lowest-performing bucket. Lean into the strength; cut the leak.</>}
      </p>

      <div className="mt-4 rounded-sm border p-3"
        style={{ background: TONE_BG.good, borderColor: TONE_BORDER.good }}>
        <p className="bt-label" style={{ color: TONE_TEXT.good }}>{HI ? "Strength" : "Strength"} · {b.dimension}</p>
        <p className="font-heading text-xl mt-1">{b.label}</p>
        <p className="text-sm mt-1.5">
          <span className="bt-num font-semibold">{b.win_rate.toFixed(2)}%</span> WR ·
          <span className="bt-num ml-1">{b.count}</span> trades · {fmt(b.pnl_usd)}
        </p>
      </div>

      <div className="mt-3 rounded-sm border p-3"
        style={{ background: TONE_BG.bad, borderColor: TONE_BORDER.bad }}>
        <p className="bt-label" style={{ color: TONE_TEXT.bad }}>{HI ? "Leak" : "Leak"} · {w.dimension}</p>
        <p className="font-heading text-xl mt-1">{w.label}</p>
        <p className="text-sm mt-1.5">
          <span className="bt-num font-semibold">{w.win_rate.toFixed(2)}%</span> WR ·
          <span className="bt-num ml-1">{w.count}</span> trades · {fmt(w.pnl_usd)}
        </p>
      </div>

      <FixList
        items={HI ? [
          `"${b.label}" ko aur dohraiye — yeh aapka highest-WR pattern hai.`,
          `"${w.label}" se bachiye ya isse refine kariye — data dikha raha hai ki yeh aapka account kha raha hai.`,
          "Agle 10 trades ke baad isse dobara dekhiye — sample grow karne par edge aur sharp hoti hai.",
        ] : [
          `Do more of "${b.label}" — it's your highest-WR pattern.`,
          `Avoid or refine "${w.label}" — your data shows it's bleeding your account.`,
          "After your next 10 trades, revisit this — your edge sharpens as the sample grows.",
        ]}
        tone="good"
        testId="drawer-edge-fixes"
      />
    </>
  );
}

// ─── Drawer wrapper ──────────────────────────────────────────────────────

const REGISTRY = {
  sl:        { title: "Stop Loss Discipline", icon: "🛡️", Body: StopLossBody, link: { to: "/trades", label: "Review trades" } },
  frequency: { title: "Trade Frequency",      icon: "⏱️", Body: FrequencyBody, link: { to: "/trades", label: "Open journal" } },
  emotional: { title: "Emotional Control",    icon: "🧘", Body: EmotionalBody, link: { to: "/trades?emotion=Revenge", label: "Review revenge trades" } },
  edge:      { title: "Your Edge",            icon: "🏆", Body: EdgeBody, link: { to: "/trades", label: "Open journal" } },
};

export default function BehavioralInsightsDrawer({ open, onOpenChange, insightKey, stats, currency, language = "en" }) {
  const cfg = insightKey ? REGISTRY[insightKey] : null;
  const Body = cfg?.Body;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="behavioral-insights-drawer"
        className="w-full sm:max-w-md bg-[var(--bt-surface)] border-[var(--bt-border)] overflow-y-auto"
      >
        {cfg && (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="font-heading text-2xl flex items-center gap-2">
                <span aria-hidden="true">{cfg.icon}</span>
                {cfg.title}
              </SheetTitle>
              <SheetDescription className="text-sm text-[var(--bt-text-muted)]">
                {language === "hi"
                  ? "Aapke trades mein is behavioral signal ka deeper view."
                  : "A deeper look at this behavioral signal in your trades."}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-5" data-testid="drawer-body">
              {Body && <Body stats={stats} currency={currency} language={language} />}
            </div>

            <div className="mt-6 flex">
              <Link
                to={cfg.link.to}
                onClick={() => onOpenChange(false)}
                data-testid="drawer-cta-link"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold rounded-sm transition-colors"
              >
                {cfg.link.label} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
